import useForceUpdate from '@/shared/hooks/useForceUpdate'
import useStateRef from '@/shared/hooks/useStateRef'
import { useEffect, useRef, useState } from 'react'

const throttleMs = 1000 / 24

const usePlayerWidget = () => {
  const forceUpdate = useForceUpdate()

  const tsFromRef = useRef<number>(null)
  const tsToRef = useRef<number>(null)

  const tsCurrentRef = useRef<number>(null)

  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1)
  const playbackSpeedRef = useStateRef<number>(playbackSpeed)

  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const isPlayingRef = useStateRef<boolean>(isPlaying)

  const animationFrameRef = useRef<number>(null)
  const lastExecuteRef = useRef<number>(null)

  const start = () => {
    if (isPlayingRef.current || !tsFromRef.current || !tsToRef.current) return
    setIsPlaying(true)

    const execute = (time: number) => {
      if (!isPlayingRef.current) return

      let dt = time - lastExecuteRef.current
      if (Number.isNaN(dt)) dt = 0

      if (dt < throttleMs) {
        animationFrameRef.current = requestAnimationFrame(execute)
        return
      }

      const next = tsCurrentRef.current + playbackSpeedRef.current * dt

      if (next >= tsToRef.current) {
        tsCurrentRef.current = tsToRef.current
        stop()
      } else tsCurrentRef.current = next

      forceUpdate()

      if (isPlayingRef.current) {
        lastExecuteRef.current = time
        animationFrameRef.current = requestAnimationFrame(execute)
      } else {
        lastExecuteRef.current = null
      }
    }

    lastExecuteRef.current = performance.now()
    animationFrameRef.current = requestAnimationFrame(execute)
  }

  const stop = () => {
    setIsPlaying(false)
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    lastExecuteRef.current = null
  }

  const updateTimeInterval = (tsFrom: number, tsTo: number) => {
    lastExecuteRef.current = null
    tsFromRef.current = tsFrom
    tsToRef.current = tsTo
    tsCurrentRef.current = tsFrom
    forceUpdate()
  }

  const seekTo = (time: number) => {
    tsCurrentRef.current = time
    stop()
    forceUpdate()
  }

  const handleChangePlay = () => {
    if (!isPlayingRef.current) {
      if (tsCurrentRef.current === tsToRef.current) tsCurrentRef.current = tsFromRef.current
      start()
    } else if (isPlayingRef.current) stop()
  }

  useEffect(() => {
    return () => stop()
  }, [])

  return {
    start,
    stop,
    updateTimeInterval,
    tsFrom: tsFromRef.current,
    tsTo: tsToRef.current,
    tsCurrent: tsCurrentRef.current,
    forceUpdate,
    handleChangePlay,
    changePlaybackSpeed: (value: number) => setPlaybackSpeed(value),
    playbackSpeed: playbackSpeed,
    isPlaying,
    seekTo,
  }
}

export default usePlayerWidget
